import { A, RouteSectionProps, useNavigate } from "@solidjs/router";
import { castTimelineStream } from "applesauce-common/observable";
import { createTimelineLoader } from "applesauce-loaders/loaders";
import { interval, mergeMap, startWith } from "rxjs";
import { createEffect, createMemo, For, from, Match, Switch } from "solid-js";

import { WifiNetwork } from "../../casts/wifi-network";
import { BackIcon } from "../../components/icons";
import UserAvatar from "../../components/user-avatar";
import UserName from "../../components/user-name";
import { WIFI_NETWORK_KIND } from "../../const";
import { cacheRequest } from "../../services/cache";
import { appRelays } from "../../services/lifestyle";
import { pool } from "../../services/pool";
import { eventStore } from "../../services/stores";

export default function ProfileNetworks(props: RouteSectionProps) {
  const { pubkey } = props.params;
  const navigate = useNavigate();

  const relays = from(appRelays);
  const loader = createMemo(() => {
    if (!pubkey || !relays()) return null;

    return createTimelineLoader(
      pool,
      relays()!,
      {
        kinds: [WIFI_NETWORK_KIND],
        authors: [pubkey],
      },
      { eventStore, cache: cacheRequest },
    );
  });

  // auto load timeline
  createEffect(() => {
    if (!loader()) return;

    return interval(10_000)
      .pipe(
        startWith(0),
        mergeMap(() => loader()!()),
      )
      .subscribe();
  });

  const networks = from(
    eventStore
      .timeline({ kinds: [WIFI_NETWORK_KIND], authors: [pubkey] })
      .pipe(castTimelineStream(WifiNetwork, eventStore)),
  );

  return (
    <>
      <main class="flex-grow overflow-auto bg-base-200 p-4">
        <div class="flex items-center gap-2 mb-6">
          <UserAvatar pubkey={pubkey} />
          <UserName pubkey={pubkey} class="text-xl font-bold" />
          <span class="text-base-content/70">Networks</span>
        </div>

        <Switch>
          <Match when={networks() && networks()!.length === 0}>
            <div class="text-center text-base-content/60 mt-8">
              No networks shared yet
            </div>
          </Match>

          <Match when={networks()}>
            <div class="space-y-4">
              <For each={networks()}>
                {(wifi) => (
                  <div class="card bg-base-100 shadow">
                    <div class="card-body p-4">
                      <A href={wifi.naddr ? `/wifi/${wifi.naddr}` : "#"}>
                        <h3 class="card-title text-primary">
                          {wifi.name || wifi.ssid}
                        </h3>
                      </A>
                      <div class="text-base-content/70">
                        <div>SSID: {wifi.ssid}</div>
                        <div class="mt-1 text-sm">{wifi.description}</div>
                      </div>
                    </div>
                  </div>
                )}
              </For>
            </div>
          </Match>
        </Switch>
      </main>

      <footer class="bg-primary text-primary-content p-2 pb-safe-or-2 flex items-center">
        <button onClick={() => navigate(-1)} class="btn btn-ghost btn-square">
          <BackIcon />
        </button>
      </footer>
    </>
  );
}
